import {
  Box,
  Button,
  Flex,
  FormErrorMessage,
  FormControl,
  FormLabel,
  Heading,
  Image,
  Input,
  Link,
  Stack,
  StackDivider,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import React, { useEffect } from 'react';
import logo from '../assets/image/logo.png';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { securedAxiosInstance } from '../api/axios';

const schema = yup
  .object({
    email: yup
      .string()
      .email('Must be a valid email')
      .required('Email is required'),
  })
  .required();

const ForgotPassword = () => {
  const { userToken } = useSelector(state => state.user);
  const navigate = useNavigate();
  // redirect authenticated user to dashboard
  useEffect(() => {
    if (userToken) {
      navigate('/dashboard');
    }
  }, [userToken, navigate]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });
  const toast = useToast();

  const onSubmit = async data => {
    try {
      const response = await securedAxiosInstance.post('/reset_request', {
        user: { email: data.email },
      });

      if (response.status === 200) {
        toast({
          description:
            'An email has been send to the address nominated. Follow the link to reset you password.',
          colorScheme: 'primary',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'bottom-right',
          variant: 'primary',
        });
        reset();
      } else {
        toast({
          title: 'Something went wrong',
          description:
            'Please try again later, or check the invitation code is correct.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    } catch (error) {
      toast({
        title: 'Something went wrong',
        description:
          'Please try again later, or check the invitation code is correct.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  };

  return (
    <Stack spacing={8} mx={'auto'} maxW={'4xl'} w={'100%'} py={12} px={6}>
      <Box rounded={'lg'} bg="white" boxShadow={'lg'}>
        <Stack
          spacing={4}
          direction={{ base: 'column', md: 'row' }}
          justify={'space-between'}
          align={'center'}
          divider={<StackDivider borderColor="gray.200" />}
        >
          <Box p={8}>
            <Image
              src={logo}
              alt="4 boxes logo"
              boxSize="300px"
              objectFit="contain"
            />
          </Box>
          <Box p={8} w={'100%'} maxW={'sm'} mx="auto">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box>
                <Heading mb="10px">Forgot Password</Heading>
                <Text mb="4" ms="4px" color="lightGray" fontWeight="400">
                  Enter your email address
                </Text>
              </Box>
              <Stack>
                <FormControl id="email" isInvalid={errors.email}>
                  <FormLabel htmlFor="email">Email address</FormLabel>
                  <Input id="email" {...register('email')} />
                  <FormErrorMessage>
                    {errors.email && errors.email.message}
                  </FormErrorMessage>
                </FormControl>
                <Stack spacing={4}>
                  <Stack
                    direction={{ base: 'column', sm: 'row' }}
                    alignItems={'start'}
                    justifyContent={'start'}
                  >
                    <Link as={RouterLink} to="/">
                      <Text color={'primary'} as="span" ms="5px">
                        Login
                      </Text>
                    </Link>
                  </Stack>
                  <Button
                    isLoading={isSubmitting}
                    isDisabled={isSubmitting}
                    type="submit"
                  >
                    Submit
                  </Button>
                </Stack>
                <Flex
                  direction="column"
                  justifyContent="center"
                  alignItems="start"
                  maxW="100%"
                  mt="0px"
                >
                  <Text fontWeight="400" fontSize="14px">
                    Not registered yet?
                    <Link as={RouterLink} to="/signup">
                      <Text
                        color={'primary'}
                        as="span"
                        ms="5px"
                        fontWeight="500"
                      >
                        Create an Account
                      </Text>
                    </Link>
                  </Text>
                </Flex>
              </Stack>
            </form>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
};

export default ForgotPassword;
